/**
 * Les photos d'une rencontre : carte de visite, flyer, stand.
 *
 * Contrairement à la photo de profil (recadrée en 320 px), une carte de visite
 * doit rester LISIBLE : on garde le cadrage d'origine et 1400 px sur le grand
 * côté, sinon l'extraction automatique n'a plus rien à lire. C'est plus lourd,
 * d'où le plafond de quatre photos par rencontre.
 *
 * L'appareil photo arrière est demandé par défaut (`capture="environment"`) :
 * on photographie une carte posée sur une table, pas son visage.
 */
import { useRef, useState } from 'react';
import { Camera, ImagePlus, X } from 'lucide-react';

const COTE_MAX = 1400;
const MAX_PHOTOS = 4;

export type TypePhoto = 'carte' | 'flyer' | 'stand' | 'autre';
export type PhotoRencontre = { url: string; type?: TypePhoto | null; ajoute_le?: string | null };

const LIBELLES: Record<TypePhoto, string> = {
  carte: 'Carte de visite',
  flyer: 'Flyer',
  stand: 'Stand',
  autre: 'Autre',
};

async function reduire(fichier: File): Promise<string> {
  const image = await new Promise<HTMLImageElement>((resoudre, rejeter) => {
    const url = URL.createObjectURL(fichier);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resoudre(img);
    };
    img.onerror = () => rejeter(new Error('Image illisible'));
    img.src = url;
  });
  const facteur = Math.min(1, COTE_MAX / Math.max(image.width, image.height));
  const toile = document.createElement('canvas');
  toile.width = Math.round(image.width * facteur);
  toile.height = Math.round(image.height * facteur);
  const ctx = toile.getContext('2d');
  if (!ctx) throw new Error('Canvas indisponible');
  ctx.drawImage(image, 0, 0, toile.width, toile.height);
  return toile.toDataURL('image/jpeg', 0.78);
}

export function PhotosRencontre({
  photos,
  onChange,
  onLire,
  lectureEnCours,
}: {
  photos: PhotoRencontre[];
  onChange: (photos: PhotoRencontre[]) => void;
  onLire?: () => void;
  lectureEnCours?: boolean;
}) {
  const appareil = useRef<HTMLInputElement>(null);
  const fichier = useRef<HTMLInputElement>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [agrandie, setAgrandie] = useState<string | null>(null);

  const ajouter = async (f: File | undefined, type: TypePhoto) => {
    if (!f) return;
    setErreur(null);
    if (photos.length >= MAX_PHOTOS) {
      setErreur(`Quatre photos par rencontre au maximum — retirez-en une d’abord.`);
      return;
    }
    try {
      onChange([...photos, { url: await reduire(f), type, ajoute_le: new Date().toISOString() }]);
    } catch {
      setErreur('Impossible de lire cette image — essayez une autre.');
    }
  };

  const aUneCarte = photos.some((p) => p.type === 'carte');

  return (
    <div className="mt-5">
      <span className="label-caps block text-fumee">Photos — carte de visite, flyer, stand</span>

      {photos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {photos.map((p, i) => (
            <figure key={i} className="relative">
              <button type="button" onClick={() => setAgrandie(p.url)} className="block">
                <img src={p.url} alt={LIBELLES[p.type ?? 'autre']} className="h-24 w-36 rounded-[8px] border border-graphite object-cover" />
              </button>
              <figcaption className="mt-1 flex items-center gap-2">
                <select
                  value={p.type ?? 'autre'}
                  onChange={(e) => onChange(photos.map((x, j) => (j === i ? { ...x, type: e.target.value as TypePhoto } : x)))}
                  className="field-input !min-h-[26px] !w-auto !border-0 !py-0 !text-[11px] !text-fumee"
                >
                  {(Object.keys(LIBELLES) as TypePhoto[]).map((t) => (
                    <option key={t} value={t} className="bg-charbon">{LIBELLES[t]}</option>
                  ))}
                </select>
              </figcaption>
              <button
                type="button"
                onClick={() => onChange(photos.filter((_, j) => j !== i))}
                aria-label="Retirer la photo"
                className="absolute -right-2 -top-2 rounded-full border border-graphite bg-charbon p-1 text-fumee hover:text-blanc"
              >
                <X size={12} strokeWidth={1.5} />
              </button>
            </figure>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => appareil.current?.click()} className="btn-creme !min-h-0 px-4 py-2 text-[13px]">
          <Camera size={13} strokeWidth={1.5} className="mr-1.5 inline" />
          Photographier la carte
        </button>
        <button type="button" onClick={() => fichier.current?.click()} className="rounded-full border border-graphite px-4 py-2 text-[13px] text-fumee hover:text-blanc">
          <ImagePlus size={13} strokeWidth={1.5} className="mr-1.5 inline" />
          Depuis mes images
        </button>
        {onLire && aUneCarte && (
          <button
            type="button"
            onClick={onLire}
            disabled={lectureEnCours}
            className="rounded-full border border-graphite px-4 py-2 text-[13px] text-blanc hover:bg-[#262626] disabled:opacity-50"
          >
            {lectureEnCours ? 'Lecture…' : 'Remplir depuis la carte'}
          </button>
        )}
        <input ref={appareil} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void ajouter(e.target.files?.[0], 'carte')} />
        <input ref={fichier} type="file" accept="image/*" hidden onChange={(e) => void ajouter(e.target.files?.[0], 'carte')} />
      </div>
      {erreur && <p className="mt-2 text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}

      {agrandie && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setAgrandie(null)}>
          <div className="absolute inset-0 bg-void/90" />
          <img src={agrandie} alt="" className="relative max-h-[90vh] max-w-[90vw] rounded-[10px] object-contain" />
        </div>
      )}
    </div>
  );
}
